/**
 * FAQ with left-hand category tabs. Each section owns its own categories: a Category block is a
 * tab, and the Questions inside it belong to it. The left nav is built here from each panel's
 * data-faq-label (nested block settings aren't readable in Liquid at the section level). Switching
 * a tab shows that category's panel and hides the others, and reflects the choice in the URL hash
 * (e.g. #shipping) so a category is directly linkable and survives refresh / back-forward.
 */

/**
 * Show one panel (and sync its section's nav). All other panels in the same section hide.
 * @param {HTMLElement} panel
 */
function faqShowPanel(panel) {
  const section = panel.closest('.faq');
  if (!section) return;
  const slug = panel.dataset.faqPanel;

  section.querySelectorAll('[data-faq-panel]').forEach((p) => {
    if (p instanceof HTMLElement) p.hidden = p !== panel;
  });
  section.querySelectorAll('[data-faq-nav] [data-faq-tab]').forEach((tab) => {
    if (!(tab instanceof HTMLElement)) return;
    const active = tab.dataset.faqTab === slug;
    tab.classList.toggle('is-active', active);
    tab.setAttribute('aria-selected', active ? 'true' : 'false');
    tab.tabIndex = active ? 0 : -1;
  });
}

/**
 * Activate a category by slug within a section. Optionally writes the slug to the URL hash without
 * scrolling (panel ids are namespaced, so nothing on the page matches a bare slug).
 * @param {Element} section
 * @param {string} slug
 * @param {boolean} [updateHash]
 */
function faqActivate(section, slug, updateHash) {
  const panel = section.querySelector(`[data-faq-panel="${CSS.escape(slug)}"]`);
  if (!(panel instanceof HTMLElement)) return false;
  faqShowPanel(panel);
  if (updateHash) history.replaceState(null, '', `#${slug}`);
  return true;
}

/**
 * Build the left nav's buttons from the section's panels.
 * @param {HTMLElement} nav
 * @param {HTMLElement[]} panels
 */
function faqBuildNav(nav, panels) {
  if (nav.dataset.faqBuilt === 'true') return;
  const fragment = document.createDocumentFragment();
  panels.forEach((panel) => {
    const slug = panel.dataset.faqPanel || '';
    if (!slug) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'faq__tab';
    button.setAttribute('role', 'tab');
    button.dataset.faqTab = slug;
    if (panel.id) button.setAttribute('aria-controls', panel.id);
    button.textContent = panel.dataset.faqLabel || slug;
    fragment.appendChild(button);
  });
  nav.appendChild(fragment);
  nav.dataset.faqBuilt = 'true';
}

/** @param {Element} section */
function faqInitSection(section) {
  const panels = /** @type {HTMLElement[]} */ (
    Array.from(section.querySelectorAll('[data-faq-panel]')).filter((p) => p instanceof HTMLElement)
  );
  if (!panels.length) return;

  const nav = section.querySelector('[data-faq-nav]');
  if (nav instanceof HTMLElement && panels.length > 1) faqBuildNav(nav, panels);

  const tabs = nav ? Array.from(nav.querySelectorAll('[data-faq-tab]')) : [];
  tabs.forEach((tab) => {
    if (!(tab instanceof HTMLElement) || tab.dataset.faqReady === 'true') return;
    tab.addEventListener('click', () => {
      if (tab.dataset.faqTab) faqActivate(section, tab.dataset.faqTab, true);
    });
    tab.dataset.faqReady = 'true';
  });

  // Initial category: the URL hash if it matches a panel in this section, else the first.
  const hash = decodeURIComponent((location.hash || '').replace(/^#/, ''));
  if (!(hash && faqActivate(section, hash, false))) {
    const firstSlug = panels[0].dataset.faqPanel;
    if (firstSlug) faqActivate(section, firstSlug, false);
  }
}

function faqInit() {
  document.querySelectorAll('.faq').forEach(faqInitSection);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', faqInit);
} else {
  faqInit();
}

// Back/forward or an external #hash link: re-activate the matching category in every section.
window.addEventListener('hashchange', () => {
  const slug = decodeURIComponent((location.hash || '').replace(/^#/, ''));
  if (!slug) return;
  document.querySelectorAll('.faq').forEach((section) => faqActivate(section, slug, false));
});

// Theme editor: re-init on section load, and open a question's category when its block is selected.
document.addEventListener('shopify:section:load', faqInit);
document.addEventListener('shopify:block:select', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const panel = target.closest('[data-faq-panel]') || target.querySelector?.('[data-faq-panel]');
  if (panel instanceof HTMLElement) faqShowPanel(panel);
});
